import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import Text from "../../Text";
import { ROUTE_PATHS } from "../../../../../constants/routeConstants";
import { useLazyFetchSearchQuery } from "../../../../../stores/api/pages.api";

const ALL_SECTIONS = [
  { key: "products", label: "Products", type: "product" },
  { key: "blogs", label: "Blogs", type: "blog" },
];

const TYPE_PLACEHOLDER = {
  product: "Search products…",
  blog: "Search blogs…",
  "": "Search products and blogs…",
};

const SearchModal = ({ onClose, searchType = "" }) => {
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const [trigger, { data: results = { products: [], blogs: [], brands: [] }, isFetching }] = useLazyFetchSearchQuery();

  const sections = searchType
    ? ALL_SECTIONS.filter((s) => s.type === searchType)
    : ALL_SECTIONS;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && handleClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (query.trim()) trigger({ q: query, type: searchType });
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query, trigger, searchType]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const hasResults = sections.some((s) => results[s.key]?.length > 0);

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex flex-col transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      {/* Modal panel */}
      <div
        className={`relative z-10 w-full bg-white shadow-2xl transition-transform duration-250 ${visible ? "translate-y-0" : "-translate-y-4"}`}
      >
        {/* Search bar row */}
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center gap-3">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={TYPE_PLACEHOLDER[searchType] ?? TYPE_PLACEHOLDER[""]}
            className="flex-1 text-sm sm:text-base text-gray-900 placeholder-gray-400 outline-none bg-transparent min-w-0"
          />
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0"
            aria-label="Close search"
          >
            <X size={20} />
          </button>
        </div>

        <div className="border-t border-gray-100" />

        {/* Results */}
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 py-4 sm:py-6 max-h-[60vh] overflow-y-auto">
          {!query.trim() ? (
            <p className="text-sm text-gray-400">Start typing to search…</p>
          ) : isFetching ? (
            <p className="text-sm text-gray-400">Searching…</p>
          ) : !hasResults ? (
            <p className="text-sm text-gray-400">No results for "{query}"</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              {sections.map(({ key, label }) => {
                const items = results[key] ?? [];
                if (items.length === 0) return null;
                return (
                  <div key={key}>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
                      {label}
                    </p>
                    <ul className="space-y-2.5">
                      {items.map((item) => (
                        <li key={item.label}>
                          <Text
                            is="a"
                            href={item.href}
                            className="text-sm text-gray-700 hover:text-blue-500 transition-colors leading-snug block"
                            onClick={handleClose}
                          >
                            {item.label}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SearchModal;
